// Canvas bbox editor — PROP-66 / S4.
//
// Replaces a floorplan_unit's rooms JSONB array. Operator interactions:
//
//   * select  — click a bbox to highlight it
//   * draw    — toggle mode; click-drag creates a rectangle that replaces
//               the selected room's bbox (or adds a new room when none
//               selected)
//   * nudge   — arrow keys move the selected bbox by 1px (10px with shift)
//   * delete  — Delete/Backspace removes the selected room
//   * relabel — type into the room's label field in the rail
//
// Coordinate system: bboxes are stored in native image pixels. Canvas
// renders at scaled size; mouse coords are translated by
// scale = canvasRect.w / naturalWidth. This keeps the persisted JSON
// pixel-exact regardless of viewport size — matches the read-only
// overlay in FloorplanInspector.tsx.
//
// Optimistic update: on save, the caller (FloorplanInspector) refetches
// the inspector so the post-refresh view equals the live one (CLAUDE.md
// "Optimistic UI must equal post-refresh UI").

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { api, ApiError } from "@/lib/api";
import { ghostBtn, primaryBtn, smallBtn } from "@/components/admin-ui";
import { inputBase } from "@/components/ui-prims";

export type Room = {
  label?: string;
  area_sqm?: number;
  bbox?: [number, number, number, number];
};

interface Props {
  open: boolean;
  unitId: number;
  unitType: string;
  imageUrl: string | undefined;
  initialRooms: Room[];
  onCancel: () => void;
  onSaved: () => void; // caller refetches inspector
}

type Draft = { x0: number; y0: number; x1: number; y1: number };

const NUDGE = 1;
const NUDGE_BIG = 10;
// Mirror of backend room*MaxLen constants. Server enforces; the dialog
// just gates the submit button so the operator gets immediate feedback
// instead of round-tripping for a 400.
const LABEL_MAX = 200;
const ROOMS_MAX = 64;

export function BBoxEditor({ open, unitId, unitType, imageUrl, initialRooms, onCancel, onSaved }: Props) {
  if (!open) return null;
  return createPortal(
    <BBoxEditorInner
      unitId={unitId}
      unitType={unitType}
      imageUrl={imageUrl}
      initialRooms={initialRooms}
      onCancel={onCancel}
      onSaved={onSaved}
    />,
    document.body,
  );
}

function BBoxEditorInner({ unitId, unitType, imageUrl, initialRooms, onCancel, onSaved }: Omit<Props, "open">) {
  const [rooms, setRooms] = useState<Room[]>(() => initialRooms.map(cloneRoom));
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [mode, setMode] = useState<"select" | "draw">("select");
  const [draft, setDraft] = useState<Draft | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [imgState, setImgState] = useState<{ w: number; h: number; loaded: boolean }>({ w: 0, h: 0, loaded: false });
  const [canvasSize, setCanvasSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  // Track rendered canvas size for native↔display coord conversion.
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const img = imgRef.current;
    if (!wrap || !img) return;
    const ro = new ResizeObserver(() => {
      const rect = img.getBoundingClientRect();
      setCanvasSize({ w: rect.width, h: rect.height });
    });
    ro.observe(img);
    return () => ro.disconnect();
  }, []);

  const scale = imgState.loaded && imgState.w > 0
    ? { x: canvasSize.w / imgState.w, y: canvasSize.h / imgState.h }
    : { x: 1, y: 1 };

  // ── Drawing ────────────────────────────────────────────────────────
  const drawOverlay = useCallback(() => {
    const cvs = canvasRef.current;
    if (!cvs || !imgState.loaded || canvasSize.w === 0) return;
    const dpr = window.devicePixelRatio || 1;
    cvs.width  = canvasSize.w * dpr;
    cvs.height = canvasSize.h * dpr;
    cvs.style.width  = `${canvasSize.w}px`;
    cvs.style.height = `${canvasSize.h}px`;
    const ctx = cvs.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, canvasSize.w, canvasSize.h);

    ctx.font = "11px ui-sans-serif, system-ui";
    rooms.forEach((r, i) => {
      if (!r.bbox || r.bbox.length !== 4) return;
      const [x0, y0, x1, y1] = r.bbox;
      const x = x0 * scale.x, y = y0 * scale.y;
      const w = (x1 - x0) * scale.x, h = (y1 - y0) * scale.y;
      const selected = i === selectedIdx;
      ctx.strokeStyle = selected ? "rgba(122,45,31,1)" : "rgba(122,45,31,0.6)";
      ctx.fillStyle   = selected ? "rgba(122,45,31,0.18)" : "rgba(122,45,31,0.06)";
      ctx.lineWidth = selected ? 2.5 : 1.25;
      ctx.fillRect(x, y, w, h);
      ctx.strokeRect(x, y, w, h);
      if (r.label) {
        const lw = ctx.measureText(r.label).width + 10;
        ctx.fillStyle = selected ? "rgba(23,32,29,0.95)" : "rgba(23,32,29,0.75)";
        ctx.fillRect(x, Math.max(0, y - 14), lw, 14);
        ctx.fillStyle = "#f4f1ea";
        ctx.fillText(r.label, x + 4, Math.max(10, y - 3));
      }
    });

    if (draft) {
      const x = Math.min(draft.x0, draft.x1) * scale.x;
      const y = Math.min(draft.y0, draft.y1) * scale.y;
      const w = Math.abs(draft.x1 - draft.x0) * scale.x;
      const h = Math.abs(draft.y1 - draft.y0) * scale.y;
      ctx.setLineDash([6, 4]);
      ctx.strokeStyle = "rgba(29,74,58,0.95)";
      ctx.fillStyle   = "rgba(29,74,58,0.08)";
      ctx.lineWidth = 1.5;
      ctx.fillRect(x, y, w, h);
      ctx.strokeRect(x, y, w, h);
      ctx.setLineDash([]);
    }
  }, [rooms, selectedIdx, draft, imgState.loaded, canvasSize, scale.x, scale.y]);

  useEffect(() => { drawOverlay(); }, [drawOverlay]);

  // ── Mouse + keyboard ───────────────────────────────────────────────
  function nativeXY(e: React.PointerEvent<HTMLCanvasElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    return {
      x: Math.max(0, Math.min(imgState.w, Math.round(cx / scale.x))),
      y: Math.max(0, Math.min(imgState.h, Math.round(cy / scale.y))),
    };
  }

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    e.currentTarget.setPointerCapture(e.pointerId);
    const pt = nativeXY(e);
    if (mode === "select") {
      const hit = hitTest(rooms, pt.x, pt.y);
      setSelectedIdx(hit);
      return;
    }
    // draw
    setDraft({ x0: pt.x, y0: pt.y, x1: pt.x, y1: pt.y });
  }
  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!draft) return;
    const pt = nativeXY(e);
    setDraft((d) => d ? { ...d, x1: pt.x, y1: pt.y } : null);
  }
  function onPointerUp(_e: React.PointerEvent<HTMLCanvasElement>) {
    if (!draft) return;
    const x0 = Math.min(draft.x0, draft.x1);
    const x1 = Math.max(draft.x0, draft.x1);
    const y0 = Math.min(draft.y0, draft.y1);
    const y1 = Math.max(draft.y0, draft.y1);
    setDraft(null);
    if (x1 - x0 < 3 || y1 - y0 < 3) return; // ignore stray clicks
    const bbox: [number, number, number, number] = [x0, y0, x1, y1];
    setRooms((prev) => {
      if (selectedIdx != null && prev[selectedIdx]) {
        const next = prev.slice();
        next[selectedIdx] = { ...next[selectedIdx], bbox };
        return next;
      }
      if (prev.length >= ROOMS_MAX) return prev;
      const newRoom: Room = { label: `Room ${prev.length + 1}`, bbox };
      const next = [...prev, newRoom];
      setSelectedIdx(next.length - 1);
      setMode("select"); // exit draw mode once new room created
      return next;
    });
  }

  // Keyboard handler attached to the modal surface (not window) so Escape
  // and arrow keys don't leak when other modals are stacked. selectedIdx
  // captured fresh per render via the dep array.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") { e.preventDefault(); onCancel(); return; }
      if (selectedIdx == null) return;
      const step = e.shiftKey ? NUDGE_BIG : NUDGE;
      let dx = 0, dy = 0;
      switch (e.key) {
        case "ArrowLeft":  dx = -step; break;
        case "ArrowRight": dx =  step; break;
        case "ArrowUp":    dy = -step; break;
        case "ArrowDown":  dy =  step; break;
        case "Delete":
        case "Backspace": {
          // Don't swallow Backspace inside text inputs.
          const tag = (e.target as HTMLElement)?.tagName;
          if (tag === "INPUT" || tag === "TEXTAREA") return;
          e.preventDefault();
          setRooms((prev) => {
            const next = prev.slice();
            next.splice(selectedIdx, 1);
            return next;
          });
          setSelectedIdx(null);
          return;
        }
        default: return;
      }
      e.preventDefault();
      setRooms((prev) => {
        const r = prev[selectedIdx];
        if (!r?.bbox) return prev;
        const [x0, y0, x1, y1] = r.bbox;
        const nx0 = clamp(x0 + dx, 0, imgState.w);
        const ny0 = clamp(y0 + dy, 0, imgState.h);
        const nx1 = clamp(x1 + dx, 0, imgState.w);
        const ny1 = clamp(y1 + dy, 0, imgState.h);
        if (nx1 - nx0 < 1 || ny1 - ny0 < 1) return prev;
        const next = prev.slice();
        next[selectedIdx] = { ...r, bbox: [nx0, ny0, nx1, ny1] };
        return next;
      });
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selectedIdx, imgState.w, imgState.h, onCancel]);

  // Lock body scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Diff against initial — only enable Save when something actually
  // changed (avoids gratuitous audit rows).
  const dirty = useMemo(() => !sameRooms(rooms, initialRooms), [rooms, initialRooms]);
  const submittable = !busy && dirty && reason.trim().length > 0
    && rooms.every((r) => (r.label ?? "").trim().length > 0 && (r.label ?? "").length <= LABEL_MAX)
    && rooms.length <= ROOMS_MAX;

  async function submit() {
    if (!submittable) return;
    setBusy(true); setErr(null);
    try {
      // Strip empty-bbox sanity check: backend allows label-only rooms
      // (no bbox key), so omit the bbox field entirely when absent.
      const payload = rooms.map((r) => {
        const out: Room = { label: (r.label ?? "").trim() };
        if (r.bbox && r.bbox.length === 4) out.bbox = r.bbox;
        if (typeof r.area_sqm === "number") out.area_sqm = r.area_sqm;
        return out;
      });
      await api.patch(`/admin/dataengine/reconcile/floorplan_unit/${unitId}/rooms`, {
        rooms: payload,
        reason: reason.trim(),
      });
      onSaved();
    } catch (e: any) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={backdrop} onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
      <div style={surface}>
        <div style={hdr}>
          <div>
            <div style={hdrTitle}>Edit rooms · {unitType || "—"}</div>
            <div style={hdrSub}>
              Drag a rectangle to {selectedIdx == null ? "create a new room" : "replace the selected room's bbox"};
              arrows nudge (shift × 10); Delete removes the selected room.
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <ModeToggle mode={mode} setMode={setMode} disabled={busy || !imgState.loaded || !imageUrl} />
            <button type="button" style={ghostBtn} onClick={onCancel} disabled={busy}>Cancel</button>
            <button
              type="button"
              style={{ ...primaryBtn, opacity: submittable ? 1 : 0.5, cursor: submittable ? "pointer" : "not-allowed" }}
              onClick={submit}
              disabled={!submittable}
            >
              {busy ? "Saving…" : "Save rooms"}
            </button>
          </div>
        </div>

        <div style={body}>
          <div ref={wrapRef} style={imgWrap}>
            {imageUrl ? (
              <img
                ref={imgRef}
                src={imageUrl}
                alt={unitType}
                draggable={false}
                style={{ display: "block", width: "100%", height: "auto", userSelect: "none" }}
                onLoad={(e) => {
                  const el = e.currentTarget;
                  setImgState({ w: el.naturalWidth, h: el.naturalHeight, loaded: true });
                  setCanvasSize({ w: el.getBoundingClientRect().width, h: el.getBoundingClientRect().height });
                }}
              />
            ) : (
              <div style={{ padding: 40, textAlign: "center", color: "var(--ink-3)", fontStyle: "italic" }}>
                No source image available for this unit.
              </div>
            )}
            <canvas
              ref={canvasRef}
              style={{
                position: "absolute",
                inset: 0,
                width: canvasSize.w || "100%",
                height: canvasSize.h || "100%",
                cursor: mode === "draw" ? "crosshair" : "default",
                pointerEvents: imgState.loaded ? "auto" : "none",
                touchAction: "none",
              }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            />
          </div>

          <RoomsRail
            rooms={rooms}
            selectedIdx={selectedIdx}
            onSelect={setSelectedIdx}
            onLabelChange={(i, label) => setRooms((prev) => {
              const next = prev.slice();
              next[i] = { ...next[i], label };
              return next;
            })}
            onRemove={(i) => {
              setRooms((prev) => prev.filter((_, idx) => idx !== i));
              setSelectedIdx(null);
            }}
            onAdd={() => {
              if (rooms.length >= ROOMS_MAX) return;
              const newRoom: Room = { label: `Room ${rooms.length + 1}` };
              setRooms((prev) => [...prev, newRoom]);
              setSelectedIdx(rooms.length);
              setMode("draw");
            }}
            busy={busy}
          />
        </div>

        <div style={ftr}>
          <label style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={fieldLabel}>Reason *</span>
            <input
              style={inputBase}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. extractor stretched bbox across legend — redrawing"
              disabled={busy}
            />
          </label>
          {err && <div style={{ color: "var(--neg)", fontSize: 12 }}>{err}</div>}
        </div>
      </div>
    </div>
  );
}

/* ─── Rooms rail ───────────────────────────────────────────────────── */

function RoomsRail({
  rooms, selectedIdx, onSelect, onLabelChange, onRemove, onAdd, busy,
}: {
  rooms: Room[];
  selectedIdx: number | null;
  onSelect: (i: number) => void;
  onLabelChange: (i: number, label: string) => void;
  onRemove: (i: number) => void;
  onAdd: () => void;
  busy: boolean;
}) {
  return (
    <div style={rail}>
      <div style={{ padding: "10px 12px", borderBottom: "1px solid var(--rule)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-4)" }}>
          Rooms · {rooms.length}
        </div>
        <button type="button" style={smallBtn} onClick={onAdd} disabled={busy || rooms.length >= ROOMS_MAX}>+ Add</button>
      </div>
      <div style={{ overflowY: "auto", flex: 1 }}>
        {rooms.length === 0 && (
          <div style={{ padding: 12, color: "var(--ink-3)", fontStyle: "italic", fontSize: 12 }}>
            No rooms — click <em>+ Add</em>, then drag a rectangle on the image.
          </div>
        )}
        {rooms.map((r, i) => {
          const selected = i === selectedIdx;
          const hasBox = r.bbox != null && r.bbox.length === 4;
          return (
            <div
              key={i}
              onClick={() => onSelect(i)}
              style={{
                padding: "8px 12px",
                borderBottom: "1px solid var(--rule)",
                background: selected ? "var(--accent-soft)" : undefined,
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  style={{ ...inputBase, padding: "5px 8px", fontSize: 12 }}
                  value={r.label ?? ""}
                  onChange={(e) => onLabelChange(i, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="(unlabeled)"
                  disabled={busy}
                  maxLength={LABEL_MAX}
                />
                <button
                  type="button"
                  style={{ ...smallBtn, padding: "3px 7px" }}
                  onClick={(e) => { e.stopPropagation(); onRemove(i); }}
                  disabled={busy}
                  title="Remove room"
                >×</button>
              </div>
              <div style={{ marginTop: 4, fontFamily: "var(--font-mono)", fontSize: 10.5, color: hasBox ? "var(--ink-4)" : "var(--neg)" }}>
                {hasBox ? `bbox [${r.bbox!.join(", ")}]` : "no bbox — drag on image to set"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ModeToggle({ mode, setMode, disabled }: { mode: "select" | "draw"; setMode: (m: "select" | "draw") => void; disabled: boolean }) {
  const segStyle = (active: boolean): CSSProperties => ({
    ...smallBtn,
    background: active ? "var(--accent)" : "var(--surface)",
    color: active ? "#f4f1ea" : "var(--ink-2)",
    border: active ? "1px solid var(--accent)" : "1px solid var(--rule)",
  });
  return (
    <div style={{ display: "inline-flex", gap: 4 }}>
      <button type="button" style={segStyle(mode === "select")} onClick={() => setMode("select")} disabled={disabled}>Select</button>
      <button type="button" style={segStyle(mode === "draw")}   onClick={() => setMode("draw")}   disabled={disabled}>Draw</button>
    </div>
  );
}

/* ─── helpers ──────────────────────────────────────────────────────── */

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

function cloneRoom(r: Room): Room {
  return {
    label: r.label,
    area_sqm: r.area_sqm,
    bbox: r.bbox ? ([...r.bbox] as [number, number, number, number]) : undefined,
  };
}

function hitTest(rooms: Room[], x: number, y: number): number | null {
  // Walk in reverse so the most-recently-drawn (visually on top) wins.
  for (let i = rooms.length - 1; i >= 0; i--) {
    const b = rooms[i].bbox;
    if (!b || b.length !== 4) continue;
    if (x >= b[0] && x <= b[2] && y >= b[1] && y <= b[3]) return i;
  }
  return null;
}

function sameRooms(a: Room[], b: Room[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if ((a[i].label ?? "") !== (b[i].label ?? "")) return false;
    if ((a[i].area_sqm ?? null) !== (b[i].area_sqm ?? null)) return false;
    const ab = a[i].bbox, bb = b[i].bbox;
    if (!ab && !bb) continue;
    if (!ab || !bb) return false;
    if (ab[0] !== bb[0] || ab[1] !== bb[1] || ab[2] !== bb[2] || ab[3] !== bb[3]) return false;
  }
  return true;
}

/* ─── styles ───────────────────────────────────────────────────────── */

const backdrop: CSSProperties = {
  position: "fixed", inset: 0,
  background: "rgba(8, 10, 12, 0.55)",
  display: "flex", alignItems: "center", justifyContent: "center",
  zIndex: 1100, padding: 20,
};
const surface: CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--rule)",
  borderRadius: 10,
  boxShadow: "0 24px 60px -12px rgba(0,0,0,0.45)",
  width: "min(1280px, 100%)",
  maxHeight: "calc(100vh - 40px)",
  display: "flex", flexDirection: "column",
  overflow: "hidden",
};
const hdr: CSSProperties = {
  display: "flex", alignItems: "flex-start", justifyContent: "space-between",
  padding: "12px 14px", borderBottom: "1px solid var(--rule)",
  background: "var(--surface)",
};
const hdrTitle: CSSProperties = {
  fontFamily: "var(--font-ui)", fontSize: 14, fontWeight: 600, color: "var(--ink)",
};
const hdrSub: CSSProperties = {
  fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--ink-3)", marginTop: 2,
};
const body: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) 280px",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
};
const imgWrap: CSSProperties = {
  position: "relative",
  background: "var(--surface-2)",
  overflow: "auto",
  borderRight: "1px solid var(--rule)",
};
const rail: CSSProperties = {
  display: "flex", flexDirection: "column",
  borderLeft: "1px solid var(--rule)",
  background: "var(--surface)",
  minHeight: 0,
};
const ftr: CSSProperties = {
  display: "flex", alignItems: "flex-end", gap: 12,
  padding: "10px 14px", borderTop: "1px solid var(--rule)",
};
const fieldLabel: CSSProperties = {
  fontFamily: "var(--font-ui)", fontSize: 10.5, fontWeight: 600,
  letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ink-4)",
};
